/**
 * ApiService by karora on 22/03/17.
 */
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, OnInit } from '@angular/core';
import { Observable, ReplaySubject, Subject } from 'rxjs';

import { StandardResponse } from '../types/standard-response';

@Injectable()
export class ApiService implements OnInit {
  static get AdminBaseURL(): string {
    return ApiService._AdminBaseURL;
  }
  private static _AdminBaseURL = '/admin/';
  private static instance: ApiService;
  private authToken: string;
  private baseHeaders: HttpHeaders;
  private tokenObserver: ReplaySubject<boolean>;
  private complained_already = false;
  loggedIn: boolean;

  public static RequestHeaders(): HttpHeaders {
    if (ApiService.instance.authToken) {
      return ApiService.instance.baseHeaders.set('Authorization', 'Bearer ' + ApiService.instance.authToken);
    }
    return ApiService.instance.baseHeaders;
  }

  public static ForgetToken(): void {
    // clear token remove user from local storage to log user out
    ApiService.instance.authToken = null;
    localStorage.removeItem('currentUser');
  }

  private static handleTokenResponse(response: StandardResponse): void {
    // console.log(`GotToken...\n${JSON.stringify(response, null, 2)}`);
    let status = response && response.status;
    if (status === 'success' && response.data && response.data.token) {
      let currentUser: any;
      try {
        currentUser = JSON.parse(localStorage.getItem('currentUser'));
      } catch (e) {
        // console.log('Nothing parseable in local storage for "currentUser"');
      }
      if (!currentUser) {
        currentUser = <any>{};
      }
      currentUser.authToken = response.data.token;
      localStorage.setItem('currentUser', JSON.stringify(currentUser));
      ApiService.instance.authToken = response.data.token;
      console.log('Stored token for future requests');
      if (!!ApiService.instance.tokenObserver) {
        ApiService.instance.tokenObserver.next(true);
      }
    }
  }

  private static handleTokenError(error: HttpErrorResponse): Error {
    // console.log('HandleTokenError...');
    let errMsg: string;
    try {
      let body = error.error || '';
      let err = body.data || JSON.stringify(body);
      errMsg = `${error.status} - ${error.statusText || ''} ${JSON.stringify(err)}`;
    } catch (e) {
      errMsg = e.message ? e.message : e.toString();
    }
    console.error(errMsg);
    try {
      ApiService.instance.tokenObserver.complete();
    } catch (e) {
      console.log('handleTokenError:' + e.message ? e.message : e.toString());
    }
    return new Error(errMsg);
  }

  private static handleTokenComplete() {
    // console.log('HandleTokenComplete...');
    try {
      ApiService.instance.tokenObserver.complete();
    } catch (e) {
      console.log('handleTokenComplete:' + e.message ? e.message : e.toString());
    }
  }

  private static authTokenValid(): boolean {
    if (!ApiService.instance.complained_already) {
      console.log('Checking for token validity (mock)...');
      ApiService.instance.complained_already = true;
    }
    // Mock for now
    return !!ApiService.instance.authToken;
  }

  private static observeNext(observer: Subject<StandardResponse>, r: StandardResponse): void {
    observer.next(r);
  }

  static AuthenticationExpired() {
    ApiService.ForgetToken();
    ApiService.instance.loggedIn = false;
  }

  static IsLoggedIn() {
    return ApiService.instance.loggedIn;
  }

  static LoginSuccessful() {
    ApiService.instance.loggedIn = true;
  }

  constructor(
    private http: HttpClient,
  ) {
    if (!ApiService.instance) {
      ApiService.instance = this;
    } else {
      console.error('We seem to be creating a second instance of AppComponent!!!');
    }
    ApiService.instance.tokenObserver = null;
    try {
      // set token if saved in local storage
      let currentUser = JSON.parse(localStorage.getItem('currentUser'));
      if (currentUser) {
        ApiService.instance.authToken = currentUser.authToken;
      }
      ApiService.instance.baseHeaders = new HttpHeaders({
        'Content-Type': 'application/json',
        'API-Key': 'f6c8d62b-768d-4c23-8b8d-8b1f9f03ae6d',
      });
    } catch (e) {
      console.log(e);
    }
  }

  ngOnInit(): void {
    try {
      console.log('OnInit...');
      this.GetToken();
    } catch (e) {
      console.log(e);
    }
  }

  GetToken(): Observable<boolean> {
    //    console.log("GetToken...");
    if (!ApiService.instance.tokenObserver) {
      //      console.log("Really GetToken...");
      ApiService.instance.tokenObserver = new ReplaySubject<boolean>();
      let url = window['apiBaseURL'] + '/auth/token';
      this.http.get(url, { headers: ApiService.RequestHeaders() })
        .subscribe(
          ApiService.handleTokenResponse,
          ApiService.handleTokenError,
          ApiService.handleTokenComplete,
        );
    }
    return ApiService.instance.tokenObserver.asObservable();
  }

  Get(endpoint: string): Observable<StandardResponse> {
    //    console.log("Get: " + url);
    let url = window['apiBaseURL'] + endpoint;
    let observer: Subject<StandardResponse> = new Subject<StandardResponse>();
    try {
      if (ApiService.authTokenValid()) {
        this.reallyGet(observer, url);
      } else {
        this.GetToken().subscribe(
          () => this.reallyGet(observer, url),
          observer.error,
        );
      }
    } catch (e) {
      console.log('Get: error: ' + e);
    }
    return observer.asObservable();
  }

  private reallyGet(observer: Subject<StandardResponse>, url: string): void {
    // console.log("GetRealURL: " + url);
    try {
      this.http.get(url, { headers: ApiService.RequestHeaders() })
        .subscribe(
          (r) => {
            // console.log('reallyGet subscribe next');
            ApiService.observeNext(observer, r as StandardResponse);
          },
          (e) => {
            if (e.status && e.status === 401) {
              ApiService.AuthenticationExpired();
              observer.error(e);
            } else {
              console.log('GetRealURL: ' + url + ' error: ' + JSON.stringify(e, null, 2));
            }
          },
        );
    } catch (e) {
      console.log('GetRealURL: error: ' + e);
    }
  }

  private responseObserver(observed: Observable<Object>): Observable<StandardResponse> {
    let observer: Subject<StandardResponse> = new Subject<StandardResponse>();
    observed.subscribe(
      v => {
        // console.log('Response from requst is: ' + JSON.stringify(v,null, 2));
        observer.next(v as StandardResponse);
      },
      e => {
        try {
          // console.log('Error from request is: ' + JSON.stringify(e,null, 2));
          if (e.status && e.status > 399 && e.status < 500) {
            if (e.status === 401) {
              ApiService.AuthenticationExpired();
            }
            // Any 4XX response should include a body which is a standard error response
            let v = e.error;
            observer.next(v as StandardResponse);
          } else {
            observer.error(e);
          }
        } catch (e2) {
          console.log("Error processing error response of:\n" + JSON.stringify(e2,null, 2));
          observer.error(e);
        }
      }
    );
    return observer;
  }
  
  Put(endpoint: string, body: any, moreHeaders: HttpHeaders = undefined): Observable<StandardResponse> {
    // console.log('Put: ' + endpoint);
    let url = window['apiBaseURL'] + endpoint;
    let putHeaders = ApiService.RequestHeaders();
    if (moreHeaders) {
      for (let hk of moreHeaders.keys()) {
        let hv = moreHeaders.get(hk);
        // console.log(`Adding header '${hk}: ${hv}`);
        putHeaders = putHeaders.set(hk, hv);
      }
    }
    return this.responseObserver(
      this.http.put(url, body, { headers: putHeaders })
    );
  }

  Post(endpoint: string, body: string): Observable<StandardResponse> {
    // console.log("Post: " + endpoint);
    let url = window['apiBaseURL'] + endpoint;
    return this.responseObserver(
      this.http.post(url, body, { headers: ApiService.RequestHeaders() })
    );
  }

  Delete(endpoint: string): Observable<StandardResponse> {
    console.log('Delete: ' + endpoint);
    let url = window['apiBaseURL'] + endpoint;
    return this.responseObserver(
      this.http.delete(url, { headers: ApiService.RequestHeaders() })
    );
  }
}
